import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import '@/App.css';
import api from './lib/api'; // Registers global axios interceptors

// Import pages
import LandingPage from './pages/LandingPage';
import AuthCallback from './pages/AuthCallback';
import UnifiedDashboard from './pages/UnifiedDashboard';
import AdminPanel from './pages/AdminPanel';
import ImpactWebsite from './pages/ImpactWebsite';
import ImpactPortal from './pages/ImpactPortal';
import WebsiteBuilder from './pages/WebsiteBuilder';
import ViewOnlyDashboard from './pages/ViewOnlyDashboard';
import BrowseCharities from './pages/BrowseCharities';
import AllCampaigns from './pages/AllCampaigns';
import MarketingTools from './pages/MarketingTools';
import CRMDashboard from './pages/CRMDashboard';
import CampaignSharePage from './pages/CampaignSharePage';
import DemoPage from './pages/DemoPage';
import SEOPage from './pages/SEOPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import PaymentResult from './pages/PaymentResult';
import DonationReceipt from './pages/DonationReceipt';
import CharityThankYou from './pages/CharityThankYou';
import PlatformShell from './components/PlatformShell';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const checkAuth = async () => {
      try {
        const response = await api.get('/api/auth/me');

        if (isMounted) {
          setUser(response.data);
          setIsAuthenticated(true);
        }
      } catch (error) {
        if (isMounted) {
          setIsAuthenticated(false);
        }
      }
    };

    checkAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  if (isAuthenticated === null) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Check role authorization
  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
          <p className="mt-2 text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return children;
};

import { Toaster } from 'sonner';

function App() {
  const location = window.location;

  // Handle session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/demo" element={<DemoPage />} />
        <Route path="/receipt/:donationId" element={<DonationReceipt />} />
        <Route path="/thank-you/:token" element={<CharityThankYou />} />
        <Route path="/solutions/:slug" element={<SEOPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/subscription/success" element={<SubscriptionSuccess />} />
        <Route path="/payment/success" element={<PaymentResult />} />
        <Route path="/payment/cancel" element={<PaymentResult cancelled />} />
        
        {/* Admin Panel - Super Admin Only */}
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <AdminPanel />
            </ProtectedRoute>
          }
        />
        
        {/* Business Dashboard - Super Admin & Business Admin */}
        <Route
          path="/dashboard/*"
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'business_admin']}>
              <PlatformShell>
                <UnifiedDashboard />
              </PlatformShell>
            </ProtectedRoute>
          }
        />
        
        {/* Analytics Dashboard - View Only Admin */}
        <Route
          path="/analytics/*"
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'business_admin', 'view_only_admin']}>
              <PlatformShell>
                <ViewOnlyDashboard />
              </PlatformShell>
            </ProtectedRoute>
          }
        />
        
        {/* Website Builder - Super Admin & Business Admin */}
        <Route
          path="/builder"
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'business_admin']}>
              <PlatformShell>
                <WebsiteBuilder />
              </PlatformShell>
            </ProtectedRoute>
          }
        />
        
        {/* Browse Charities - Business Admin */}
        <Route
          path="/charities/browse"
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'business_admin']}>
              <PlatformShell>
                <BrowseCharities />
              </PlatformShell>
            </ProtectedRoute>
          }
        />
        
        {/* Charity Map (alias) - Business Admin */}
        <Route
          path="/charities/map"
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'business_admin']}>
              <PlatformShell>
                <BrowseCharities />
              </PlatformShell>
            </ProtectedRoute>
          }
        />
        
        {/* All Campaigns & Reports - Business Admin */}
        <Route
          path="/campaigns/all"
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'business_admin', 'view_only_admin']}>
              <PlatformShell>
                <AllCampaigns />
              </PlatformShell>
            </ProtectedRoute>
          }
        />
        
        {/* Marketing Tools - Business Admin */}
        <Route
          path="/marketing"
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'business_admin']}>
              <PlatformShell>
                <MarketingTools />
              </PlatformShell>
            </ProtectedRoute>
          }
        />
        
        {/* CRM Dashboard - Super Admin Only */}
        <Route
          path="/crm/*"
          element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <CRMDashboard />
            </ProtectedRoute>
          }
        />
        
        {/* Public Impact Website - No Auth Required */}
        <Route path="/impact/:businessId" element={<ImpactPortal />} />
        
        {/* Public Impact Portal Success Page - No Auth Required */}
        <Route path="/impact/:businessId/success" element={<ImpactPortal />} />
        
        {/* Public Campaign Share Page - No Auth Required */}
        <Route path="/impact/:businessId/campaign/:campaignId" element={<CampaignSharePage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
